import { t, useStoreLanguage, getDirection } from "../../../features/store-language/i18n.ts";
import { Button, Tooltip } from "antd";
import { GoogleOutlined, SafetyCertificateOutlined } from "@ant-design/icons";
import { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import AuthenticatedImage from "../../shared/AuthenticatedImage.tsx";
import LanguageSelector from "../../../features/store-language/LanguageSelector.tsx";

type CustomerAuthShellProps = {
  companyInfo: any;
  title: string;
  description: string;
  children: ReactNode;
  footer?: ReactNode;
  showAlternativeMethods?: boolean;
  variant?: "login" | "login-status" | "register" | "invitation" | "invitation-status";
  statusIcon?: ReactNode;
  eyebrow?: string;
  showLanguageSelector?: boolean;
};

const CustomerAuthShell = ({
  companyInfo,
  title,
  description,
  children,
  footer,
  showAlternativeMethods = false,
  variant = "login",
  statusIcon,
  eyebrow,
  showLanguageSelector = false,
}: CustomerAuthShellProps) => {
  useStoreLanguage();
  const navigate = useNavigate();
  const companyTitle = companyInfo?.title || companyInfo?.name || t("شرکت");

  function navigateToShop() {
    navigate("/products");
  }

  return (
    <main
      className={`customer-auth-page customer-auth-page--${variant}`}
      dir={getDirection()}
    >
      <div className="customer-auth-backdrop" aria-hidden="true" />

      <section
        className={`customer-auth-shell customer-auth-shell--${variant}`}
        aria-labelledby="customer-auth-title"
      >
        <header className="customer-auth-topbar">
          <button
            type="button"
            className={companyInfo?.fileId ? "customer-auth-brand" : "customer-auth-brand customer-auth-brand--text-only"}
            onClick={navigateToShop}
            aria-label={t("بازگشت به کاتالوگ")}
          >
            {companyInfo?.fileId ? (
              <AuthenticatedImage
                fileId={String(companyInfo?.fileId ?? "")}
                disablePreview={true}
                originalImage={false}
                className="company-auth-logo"
                alt={t("نشان {{value0}}", { value0: companyTitle })}
              />
            ) : null}
            <span className="customer-auth-brand-copy">
              <strong>{companyTitle}</strong>
            </span>
          </button>
          {showLanguageSelector ? <LanguageSelector /> : null}
        </header>

        <div className="customer-auth-content">
          <div className="customer-auth-intro">
            {statusIcon ? (
              <div
                className="customer-auth-status-icon"
                aria-hidden="true"
              >
                {statusIcon}
              </div>
            ) : null}
            {eyebrow ? <span className="customer-auth-status-eyebrow">{eyebrow}</span> : null}
            <h1 id="customer-auth-title">{title}</h1>
            <p>{description}</p>
          </div>

          <div className="customer-auth-form-area">{children}</div>

          {showAlternativeMethods ? (
            <div
              className="customer-auth-alternatives"
              aria-label={t("روش‌های دیگر ورود")}
            >
              <div className="customer-auth-divider">
                <span>{t("یا ورود با روش‌های دیگر")}</span>
              </div>
              <div className="customer-auth-methods">
                <Tooltip title={t("به‌زودی فعال می‌شود")}>
                  <Button disabled icon={<GoogleOutlined />}>{t("ورود با Google")} </Button>
                </Tooltip>
              </div>
            </div>
          ) : null}

          {footer ? <div className="customer-auth-footer">{footer}</div> : null}
        </div>
        <footer className="customer-auth-trust-footer">
          <a
            className="customer-auth-raj-signature"
            href="https://rajsystem.com"
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t("طراحی و توسعه توسط رَج")}
          >
            <span>{t("طراحی و توسعه توسط")}</span>
            <strong>{t("رَج")}</strong>
          </a>
          <span className="customer-auth-trust-divider" aria-hidden="true" />
          <div className="customer-auth-security">
            <SafetyCertificateOutlined />
            <span>{t("اطلاعات شما نزد ما امن و محرمانه است.")}</span>
          </div>
        </footer>
      </section>
    </main>
  );
};

export default CustomerAuthShell;
